import React, { useMemo } from 'react'

import { isNil } from 'lodash-es'

import {
  ProviderInsightsCompetencyAreaLabels,
  ProviderInsightsCompetencyCategory,
} from '@lyrahealth-inc/shared-app-logic/src/features/careInsights/constants'
import { formatNumberToDecimalPlaces } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/stringUtils'
import { CareInsightsMetrics } from '@lyrahealth-inc/shared-app-logic/src/models/careInsights/CareInsights'
import {
  CareInsightsSubsection,
  CareInsightsSubsectionProps,
} from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/careInsightsSubsection/CareInsightsSubsection'

interface TreatmentPlanningInsightsProps extends Partial<CareInsightsSubsectionProps> {
  metricInsights?: CareInsightsMetrics
}

export const TreatmentPlanningInsights: React.FC<TreatmentPlanningInsightsProps> = ({
  aiInsightsCurrentMonth,
  aiInsightsPastMonth,
  getClientInfoFromIds,
  isLoadingClients,
  isLoadingCareInsightsMetrics,
  isLoadingProviderInsights,
  isSupervisor,
  metricInsights,
  onClientPress,
  onSkillPress,
  onSupervisorUsageGuidelinesPress,
}) => {
  const treatmentPlanningMetrics = useMemo(() => {
    if (!metricInsights) {
      return []
    }
    const {
      clinical_success_rate: clinicalSuccessRate,
      activity_completion_rate: activityCompletionRate,
      activity_completion_program_average: activityCompletionProgramAverage,
      assignment_rate: assignmentRate,
      treatment_duration: treatmentDuration,
      treatment_duration_program_average: treatmentDurationProgramAverage,
      assessment_completion_rate: assessmentCompletionRate,
      assessment_completion_program_average: assessmentCompletionProgramAverage,
      feedback_rate: feedbackRate,
    } = metricInsights

    return [
      {
        title: 'Clinical success',
        value: isNil(clinicalSuccessRate)
          ? undefined
          : formatNumberToDecimalPlaces({ value: clinicalSuccessRate, isPercentage: true }),
        goal: '80%',
        tooltipContent: 'See Measurement Based Care tab for further information.',
        testID: 'ClinicalSuccess',
      },
      {
        title: 'Activity completion',
        value: isNil(activityCompletionRate)
          ? undefined
          : formatNumberToDecimalPlaces({ value: activityCompletionRate, isPercentage: true }),
        programAverage: isNil(activityCompletionProgramAverage)
          ? 'N/A'
          : formatNumberToDecimalPlaces({ value: activityCompletionProgramAverage, isPercentage: true }),
        tooltipContent: 'Completed and dropout episodes only',
        testID: 'ActivityCompletion',
      },
      {
        title: 'Assignment rate',
        value: isNil(assignmentRate)
          ? undefined
          : formatNumberToDecimalPlaces({ value: assignmentRate, isPercentage: true }),
        goal: '80%',
        tooltipContent: 'See Platform Engagement tab',
        testID: 'AssignmentRate',
      },
      {
        title: 'Treatment duration (weeks)',
        value: isNil(treatmentDuration) ? undefined : formatNumberToDecimalPlaces({ value: treatmentDuration }),
        programAverage: isNil(treatmentDurationProgramAverage)
          ? 'N/A'
          : formatNumberToDecimalPlaces({ value: treatmentDurationProgramAverage }),
        tooltipContent: 'Completed and dropout episodes only',
        testID: 'TreatmentDuration',
      },
      {
        title: 'Assessment completion',
        value: isNil(assessmentCompletionRate)
          ? undefined
          : formatNumberToDecimalPlaces({ value: assessmentCompletionRate, isPercentage: true }),
        programAverage: isNil(assessmentCompletionProgramAverage)
          ? 'N/A'
          : formatNumberToDecimalPlaces({ value: assessmentCompletionProgramAverage, isPercentage: true }),
        tooltipContent: 'Completed and dropout episodes only',
        testID: 'AssessmentCompletion',
      },
      {
        title: 'Feedback rate',
        value: isNil(feedbackRate)
          ? undefined
          : formatNumberToDecimalPlaces({ value: feedbackRate, isPercentage: true }),
        goal: '25%',
        tooltipContent: 'See Platform Engagement tab',
        testID: 'FeedbackRate',
      },
    ]
  }, [metricInsights])

  return (
    <CareInsightsSubsection
      title={ProviderInsightsCompetencyAreaLabels[ProviderInsightsCompetencyCategory.TREATMENT_PLANNING]}
      competencyAreaName={ProviderInsightsCompetencyCategory.TREATMENT_PLANNING}
      testIDPrefix='TreatmentPlanningInsights'
      aiInsightsCurrentMonth={aiInsightsCurrentMonth}
      aiInsightsPastMonth={aiInsightsPastMonth}
      getClientInfoFromIds={getClientInfoFromIds}
      isLoadingClients={isLoadingClients}
      isLoadingCareInsightsMetrics={isLoadingCareInsightsMetrics}
      isLoadingProviderInsights={isLoadingProviderInsights}
      isSupervisor={isSupervisor}
      metrics={treatmentPlanningMetrics}
      metricsPerRow={3}
      onClientPress={onClientPress}
      onSkillPress={onSkillPress}
      onSupervisorUsageGuidelinesPress={onSupervisorUsageGuidelinesPress}
    />
  )
}
